import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ArrowRight, Zap, Brain, Code, Database, Users, TrendingUp, CheckCircle, Calendar, Clock, DollarSign, Star, ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import tulioIcon from './assets/tulio.png'
import Navigation from './Navigation';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    budget: '',
    timeline: '',
    details: ''
  });

  const serviceDetails = {
    'full-product-development': {
      title: "Full Product Development",
      subtitle: "End-to-end digital product development from concept to scale",
      icon: Code,
      color: "from-emerald-500 to-teal-600",
      pricing: "Starting from $50k",
      timeline: "12-24 weeks",
      overview: "Transform your vision into a scalable digital product with our comprehensive development approach. We handle everything from initial concept and user research to deployment and post-launch optimization.",
      process: [
        { phase: "Discovery & Planning", duration: "2-3 weeks", description: "User research, technical architecture, project roadmap" },
        { phase: "Design & Prototyping", duration: "3-4 weeks", description: "UX/UI design, interactive prototypes, user testing" },
        { phase: "Development", duration: "6-12 weeks", description: "Agile development, regular demos, quality assurance" },
        { phase: "Launch & Scale", duration: "2-3 weeks", description: "Deployment, monitoring, performance optimization" }
      ],
      features: [
        "Complete technical architecture design",
        "Modern tech stack (React, Node.js, Cloud-native)",
        "Mobile-responsive design",
        "API development and integrations",
        "Database design and optimization",
        "DevOps and CI/CD setup",
        "Security implementation",
        "Performance optimization",
        "Post-launch support (3 months included)"
      ],
      technologies: ["React", "Node.js", "Python", "AWS/GCP", "MongoDB", "PostgreSQL", "Docker", "Kubernetes"],
      caseStudies: [
        { title: "Bigfoot Logistics Platform", result: "40% efficiency increase", industry: "Logistics" },
        { title: "Cristofori ERP System", result: "50+ locations unified", industry: "Retail" }
      ]
    },
    'ai-consulting': {
      title: "AI Consulting & Agent Development",
      subtitle: "Intelligent automation and AI-powered solutions for business transformation",
      icon: Brain,
      color: "from-cyan-500 to-blue-600",
      pricing: "Starting from $25k",
      timeline: "6-12 weeks",
      overview: "Leverage the power of AI to automate workflows, enhance decision-making, and create intelligent systems that learn and adapt to your business needs.",
      process: [
        { phase: "AI Strategy & Assessment", duration: "1-2 weeks", description: "Current state analysis, AI opportunity identification, ROI planning" },
        { phase: "Solution Design", duration: "2-3 weeks", description: "AI architecture design, model selection, integration planning" },
        { phase: "Development & Training", duration: "3-6 weeks", description: "AI model development, training, testing, and optimization" },
        { phase: "Deployment & Monitoring", duration: "1-2 weeks", description: "Production deployment, monitoring setup, performance tracking" }
      ],
      features: [
        "Custom AI agent development",
        "Workflow automation solutions",
        "Natural language processing",
        "Computer vision applications",
        "Predictive analytics models",
        "Legacy system AI integration",
        "MLOps pipeline setup",
        "AI governance and compliance",
        "Ongoing model optimization"
      ],
      technologies: ["OpenAI GPT", "LangChain", "TensorFlow", "PyTorch", "Hugging Face", "AWS SageMaker", "MLflow", "Vector Databases"],
      caseStudies: [
        { title: "Automated Document Processing", result: "80% time reduction", industry: "Finance" },
        { title: "Predictive Maintenance System", result: "60% downtime reduction", industry: "Manufacturing" }
      ]
    },
    'technology-consulting': {
      title: "Technology Consulting & Integration",
      subtitle: "Strategic technical guidance and system optimization",
      icon: Zap,
      color: "from-orange-500 to-red-600",
      pricing: "Starting from $15k",
      timeline: "4-8 weeks",
      overview: "Navigate complex technical challenges with expert guidance. We help optimize your existing systems, plan technology roadmaps, and execute seamless integrations.",
      process: [
        { phase: "Technical Assessment", duration: "1 week", description: "Current system analysis, bottleneck identification, security audit" },
        { phase: "Strategy Development", duration: "1-2 weeks", description: "Technology roadmap, architecture recommendations, cost analysis" },
        { phase: "Implementation", duration: "2-4 weeks", description: "System optimization, integration development, migration execution" },
        { phase: "Optimization", duration: "1 week", description: "Performance tuning, monitoring setup, documentation" }
      ],
      features: [
        "Technology stack evaluation",
        "System architecture review",
        "API development and integration",
        "Database optimization",
        "Security implementation",
        "Performance optimization",
        "Legacy system modernization",
        "Cloud migration strategy",
        "Technical documentation"
      ],
      technologies: ["Microservices", "REST APIs", "GraphQL", "Redis", "Elasticsearch", "Kafka", "Jenkins", "Terraform"],
      caseStudies: [
        { title: "E-commerce Platform Migration", result: "99.9% uptime achieved", industry: "Retail" },
        { title: "Banking System Integration", result: "50% faster transactions", industry: "Finance" }
      ]
    },
    'staff-augmentation': {
      title: "Staff Augmentation & Team Scaling",
      subtitle: "Global engineering talent to scale your team efficiently",
      icon: Users,
      color: "from-violet-500 to-purple-600",
      pricing: "From $3k/month per engineer",
      timeline: "2-4 weeks onboarding",
      overview: "Quickly scale your development capacity with our vetted global engineering talent. Seamlessly integrate skilled developers into your existing workflows and culture.",
      process: [
        { phase: "Requirements Analysis", duration: "3-5 days", description: "Skill assessment, team structure analysis, cultural fit evaluation" },
        { phase: "Talent Matching", duration: "1 week", description: "Candidate screening, technical interviews, team introductions" },
        { phase: "Onboarding", duration: "1-2 weeks", description: "System access setup, process training, team integration" },
        { phase: "Performance Management", duration: "Ongoing", description: "Regular check-ins, performance reviews, skill development" }
      ],
      features: [
        "Vetted senior engineers",
        "Flexible engagement models",
        "Cultural integration support",
        "Multiple time zone coverage",
        "Agile methodology expertise",
        "Technology specialization",
        "Performance monitoring",
        "Team leadership options",
        "Knowledge transfer protocols"
      ],
      technologies: ["Full-stack Development", "DevOps", "Cloud Engineering", "Data Engineering", "AI/ML", "Mobile Development", "QA/Testing", "Product Management"],
      caseStudies: [
        { title: "Fintech Scaling", result: "3x development speed", industry: "Finance" },
        { title: "Healthcare Platform", result: "40% cost reduction", industry: "Healthcare" }
      ]
    },
    'data-engineering': {
      title: "Data Engineering & ML Operations",
      subtitle: "Transform data into competitive advantage with scalable platforms",
      icon: Database,
      color: "from-teal-500 to-green-600",
      pricing: "Starting from $30k",
      timeline: "8-16 weeks",
      overview: "Build robust data infrastructure that turns your data into actionable insights. From data pipelines to ML model deployment, we create scalable data solutions.",
      process: [
        { phase: "Data Architecture Design", duration: "2 weeks", description: "Data audit, architecture planning, technology selection" },
        { phase: "Pipeline Development", duration: "4-6 weeks", description: "ETL/ELT pipelines, data warehousing, real-time processing" },
        { phase: "Analytics & ML Setup", duration: "3-4 weeks", description: "Dashboard creation, ML model development, deployment pipelines" },
        { phase: "Monitoring & Optimization", duration: "1-2 weeks", description: "Performance monitoring, data quality checks, optimization" }
      ],
      features: [
        "Data lake and warehouse setup",
        "Real-time data pipelines",
        "Business intelligence dashboards",
        "Machine learning model deployment",
        "Data quality management",
        "Stream processing solutions",
        "API development for data access",
        "Data governance implementation",
        "Scalable cloud infrastructure"
      ],
      technologies: ["Apache Spark", "Kafka", "Airflow", "dbt", "Snowflake", "BigQuery", "Tableau", "Power BI"],
      caseStudies: [
        { title: "Retail Analytics Platform", result: "25% revenue increase", industry: "Retail" },
        { title: "IoT Data Processing", result: "Real-time insights", industry: "Manufacturing" }
      ]
    },
    'growth-support': {
      title: "Growth & Capital Support",
      subtitle: "Strategic partnerships, funding, and market expansion support",
      icon: TrendingUp,
      color: "from-indigo-500 to-blue-600",
      pricing: "Success-based pricing",
      timeline: "Varies by scope",
      overview: "Beyond technology development, we help you secure funding, access grants, and build strategic partnerships for sustainable growth.",
      process: [
        { phase: "Growth Strategy", duration: "2-3 weeks", description: "Market analysis, growth planning, funding strategy development" },
        { phase: "Funding Preparation", duration: "4-6 weeks", description: "Pitch deck creation, financial modeling, investor readiness" },
        { phase: "Application & Outreach", duration: "6-12 weeks", description: "Grant applications, investor meetings, partnership negotiations" },
        { phase: "Execution Support", duration: "Ongoing", description: "Implementation support, milestone tracking, continued guidance" }
      ],
      features: [
        "Grant identification and application",
        "Investor pitch deck development",
        "Financial modeling and valuation",
        "Strategic partnership facilitation",
        "Market entry strategy",
        "Regulatory compliance guidance",
        "Technology due diligence support",
        "Go-to-market strategy development",
        "International expansion planning"
      ],
      technologies: ["Business Intelligence", "Market Research Tools", "Financial Modeling", "CRM Systems", "Analytics Platforms", "Compliance Tools"],
      caseStudies: [
        { title: "Series A Fundraising", result: "$5M raised", industry: "SaaS" },
        { title: "Government Grant Success", result: "$500K secured", industry: "CleanTech" }
      ]
    }
  };

  const service = serviceDetails[serviceId];
  if (!service) {
    return <div>Service not found</div>;
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your interest! We will send you a custom quote within 24 hours.');
    setShowQuoteForm(false);
  };

  const Icon = service.icon;

  return (
    <div className="min-h-screen bg-white">
       {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
             <Link to="/" className="flex items-center space-x-3">
            <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <Navigation />
          </div>
        </div>
      </header>

      {/* Service Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Services
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6`}>
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">{service.title}</h1>
              <p className="text-xl text-slate-600 mb-8">{service.subtitle}</p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setShowQuoteForm(true)}
                >
                  Get Free Quote
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Call
                </Button>
              </div>
              
              <div className="flex items-center space-x-8 text-slate-600">
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">{service.pricing}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">{service.timeline}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-xl font-bold mb-4">Quick Facts</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-slate-600">Investment:</span>
                  <span className="font-semibold">{service.pricing}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Timeline:</span>
                  <span className="font-semibold">{service.timeline}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Guarantee:</span>
                  <span className="font-semibold">Money-back guarantee</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  </div>
                  <p className="text-sm text-slate-600">5.0 rating from 50+ clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Overview</h2>
          <p className="text-lg text-slate-600 leading-relaxed">{service.overview}</p>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Our Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((phase, index) => (
              <Card key={index} className="text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-bold mb-2">{phase.phase}</h3>
                  <p className="text-sm text-emerald-600 mb-3">{phase.duration}</p>
                  <p className="text-sm text-slate-600">{phase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features & Technologies */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">What's Included</h2>
              <ul className="space-y-3">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold mb-6">Technologies We Use</h2>
              <div className="grid grid-cols-2 gap-4">
                {service.technologies.map((tech, index) => (
                  <div key={index} className="bg-slate-100 rounded-lg p-3 text-center">
                    <span className="text-slate-700 font-medium">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Modal */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <h3 className="text-2xl font-bold mb-4">Get Your Free Quote</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <Input
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <Input
                  name="company"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={handleInputChange}
                />
                <Input
                  name="budget"
                  placeholder="Budget Range"
                  value={formData.budget}
                  onChange={handleInputChange}
                />
                <Input
                  name="timeline"
                  placeholder="Desired Timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                />
                <Textarea
                  name="details"
                  placeholder="Project Details"
                  value={formData.details}
                  onChange={handleInputChange}
                  rows={3}
                />
                <div className="flex gap-3">
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    Get Quote
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowQuoteForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's discuss your project and create a custom solution that drives results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setShowQuoteForm(true)}
            >
              Get Free Quote
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-slate-400 text-white hover:bg-white/10"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Call
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;